import React, { useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { useSearchParams } from "react-router-dom";
import Spinner from "../spinner/Spinner";
import { useVendorReconciliation, useSupplyVendorsDropdown } from "../../hooks/useInventory";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";

import formatDate from "../../components/utils/DateFormatter";
const VendorReconciliation = () => {
  const [searchParams] = useSearchParams();
  const presetVendorId = searchParams.get("vendor_id") || "";

  const [vendorId, setVendorId] = useState(presetVendorId);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data: vendorsData } = useSupplyVendorsDropdown();
  const { data, isLoading } = useVendorReconciliation(vendorId, { start_date: startDate, end_date: endDate });

  const vendors = vendorsData?.data || [];
  const ledger = data?.data?.ledger || [];
  const summary = data?.data?.summary || {};

  const balanceColor = (balance) => {
    if (balance > 0) return "text-green-600";
    if (balance < 0) return "text-red-600";
    return "text-gray-700";
  };

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return formatDate(dateStr, true, true);
  };

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Vendor Reconciliation</h1>
          <p className="text-gray-500 text-sm mt-1">Chronological ledger of purchases vs payments</p>
        </div>

        {/* Vendor selector */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3">
          <select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1 flex-1 min-w-[200px]"
          >
            <option value="">Select a vendor</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1" />
        </div>

        {!vendorId ? (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-400">
            <p className="text-lg">Select a vendor to view reconciliation</p>
          </div>
        ) : isLoading ? (
          <Spinner loading />
        ) : (
          <>
            {/* Summary */}
            {summary.total_purchased != null && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  { label: "Total Purchased", value: summary.total_purchased, color: "text-gray-800" },
                  { label: "Total Paid", value: summary.total_paid, color: "text-green-600" },
                  { label: "Net Balance", value: summary.net_balance, color: summary.balance_type === "deficit" ? "text-red-600" : "text-green-600" },
                ].map((card) => (
                  <div key={card.label} className="bg-white rounded-lg shadow p-5">
                    <p className="text-gray-500 text-sm">{card.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${card.color}`}>
                      <CurrencyFormatter amount={parseFloat((card.value || "0").toString().replace(/,/g, "")) } />
                      {card.label === "Net Balance" && summary.balance_type && (
                        <span className="text-sm ml-1 font-normal capitalize">({summary.balance_type})</span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Ledger table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-800">Transaction Ledger</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Date", "Type", "Description", "Debit (Purchase)", "Credit (Payment)", "Running Balance"].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ledger.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-gray-500">No transactions found for this vendor.</td>
                      </tr>
                    ) : (
                      ledger.map((entry, i) => (
                        <tr key={i} className={`hover:bg-gray-50 ${entry.type === "purchase" ? "bg-red-50/30" : "bg-green-50/30"}`}>
                          <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDateTime(entry.date)}</td>
                          <td className="px-5 py-3">
                            <span style={{
                              padding: "2px 8px", borderRadius: "9999px", fontSize: "0.7rem", fontWeight: 600,
                              backgroundColor: entry.type === "purchase" ? "#fee2e2" : "#dcfce7",
                              color: entry.type === "purchase" ? "#991b1b" : "#166534",
                            }}>
                              {entry.type}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-700 max-w-xs">
                            <div className="font-medium text-gray-800">{entry.order_number || "-"}</div>
                            {entry.narration && <div className="text-xs text-gray-500 mt-0.5 truncate" title={entry.narration}>{entry.narration}</div>}
                            {entry.details && <div className="text-xs text-gray-400 mt-0.5 truncate" title={entry.details}>{entry.details}</div>}
                          </td>
                          <td className="px-5 py-3 text-sm text-red-600 font-medium">
                            {entry.type === "purchase" ? <CurrencyFormatter amount={entry.total_amount || 0} /> : "-"}
                          </td>
                          <td className="px-5 py-3 text-sm text-green-600 font-medium">
                            {entry.type === "payment" ? <CurrencyFormatter amount={entry.amount_paid || 0} /> : "-"}
                          </td>
                          <td className={`px-5 py-3 text-sm font-bold ${entry.balance_type === "deficit" ? "text-red-600" : "text-green-600"}`}>
                            <CurrencyFormatter amount={parseFloat((entry.balance || "0").toString().replace(/,/g, ""))} />
                            {entry.balance_type && <span className="text-xs ml-1 font-normal capitalize">({entry.balance_type})</span>}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayouts>
  );
};

export default VendorReconciliation;
